// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — export.dxf streaming PRODUCER (ABB-S6.3, SM-9) [HIGH risk]
// ═══════════════════════════════════════════════════════════════════════════
//
// The `export.dxf` rpc producer: streams the committed-document DXF to the companion's
// `ExportPump` as `export-chunk*` frames, then resolves `{ok:true, result:{byteLength,
// snapshotRevision}}` (→ `export-complete`, `serializeSuccessTerminal`) or `{ok:false}`
// (→ `export-error`). The HARD invariants (design §3.2 / SM-9 / [H2]):
//
//   • CAPTURE-ONCE [H2]: read the FULL exported DXF STRING ONCE synchronously up front
//     (the DI `getExportedDxfText`, = `App.tsx`'s `exportedDxfTextRef.current()`), pin the
//     `snapshotRevision` + the ORIGIN epoch, then slice EVERY chunk from that IMMUTABLE
//     captured STRING. A non-bridge UI edit committing mid-export NEVER changes the bytes
//     (the captured string is isolated) — the producer NEVER re-derives mid-stream.
//   • INCREMENTAL ENCODE (#377 P2): we NEVER materialize the whole UTF-8 byte array up front
//     (`TextEncoder().encode(dxfText)` would hold a SECOND full-size copy of a hundreds-of-MiB
//     export + block the event loop before a pause/cancel could be observed). Instead we slice
//     the captured STRING by CHAR per chunk (`nextChunkBoundary`, ≤ EXPORT_CHUNK_BYTES, never
//     mid-surrogate) and encode ONLY that chunk's substring to bytes ON DEMAND — at most one
//     chunk's worth of bytes is ever resident. `byteLength` is ACCUMULATED from the per-chunk
//     encoded lengths.
//   • STANDARD base64 (`+`/`/`, NOT base64url): the companion STRICT-validates canonical
//     standard base64 PER CHUNK + aborts no-trailer on any non-canonical char (`exportBroker.ts`:
//     419/465-482). Each chunk is base64-encoded INDEPENDENTLY (its own trailing padding is
//     fine — the companion decodes each chunk separately then appends the DECODED bytes,
//     `exportBroker.ts`:327-333, so interior padding never crosses a chunk boundary).
//   • DECODED-byteLength: `export-complete.byteLength` = the SUM of the DECODED chunk bytes
//     (the accumulated per-chunk UTF-8 byte length of the captured string), NOT string.length,
//     NOT the base64 length. The companion ABORTS on a mismatch (`exportBroker.ts`:368-383).
//   • FLOW CONTROL: honor RECEIVED `export-pause`/`-resume`/`-cancel` (the 16 MiB credit
//     cap is companion-side; we pause our producer on `export-pause`, resume on
//     `export-resume`, abort on `export-cancel`). A pause-then-cancel-without-resume still
//     aborts (cancel wins).
//   • EPOCH-HALT: bind the producer to the ORIGIN epoch + the connection liveness. On a
//     `session-changed`/`welcome` epoch bump OR a socket drop mid-export, STOP — emit
//     nothing further + resolve a `{ok:false}` abort (the companion already aborted the
//     HTTP response). The DI `isExportStillValid()` predicate is the liveness/epoch gate.
//   • SINGLE-PRODUCER: at most ONE resident captured string. A 2nd concurrent export while
//     one runs is REFUSED `{ok:false, code:'export-in-flight'}` (the companion also single-
//     latches, `exportBroker.ts` [L8] — this is the browser-side belt-and-braces).
//
// DEP-GRAPH (ПРИОРИТЕТ #2): imports ONLY the sibling codec builder + clientTypes. EVERY
// coupling (the DXF source, the chunk-send sink, the epoch/liveness predicate, the
// snapshotRevision getter) arrives via `ExportDxfHandlerDeps` DI — NO editorStore, NO App.
// `npm run deps:graph` cycles=0.
// ═══════════════════════════════════════════════════════════════════════════

import type { InboundExportControlFrame, OutboundExportChunkFrame } from "./wsFrameCodec";
import type { RpcHandler, RpcHandlerContext, RpcHandlerOutcome } from "./clientTypes";

/**
 * The per-chunk byte CAP. The incremental encoder cuts each chunk at the largest CHAR boundary
 * whose UTF-8 byte length is ≤ this (so a chunk may be SLIGHTLY under the cap when a multi-byte
 * char straddles it). Each chunk is base64-encoded INDEPENDENTLY (its own trailing padding is
 * canonical; the companion decodes each chunk separately, `exportBroker.ts`:327-333).
 */
export const EXPORT_CHUNK_BYTES = 60 * 1024; // 61 440 B.

/** A cooperative yield so a large export does not block the event loop for long. */
const microYield = (): Promise<void> =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, 0);
  });

/**
 * The UTF-8 byte length of a single Unicode code point (NO allocation — pure arithmetic). The
 * incremental encoder uses it to find a CHAR boundary at/under the per-chunk byte cap WITHOUT
 * materializing the whole byte array: ASCII `< 0x80` = 1, `< 0x800` = 2, `< 0x10000` = 3, else
 * (an astral code point, a UTF-16 surrogate pair) = 4. A char boundary is ALWAYS a valid UTF-8
 * boundary (a multi-byte char is never split — `for…of` iterates whole code points), so the
 * per-chunk substring encodes wholly + decodes back losslessly.
 */
function utf8ByteLengthOfCodePoint(codePoint: number): number {
  if (codePoint < 0x80) return 1;
  if (codePoint < 0x800) return 2;
  if (codePoint < 0x10000) return 3;
  return 4; // an astral code point (a surrogate pair in UTF-16) → 4 UTF-8 bytes.
}

/**
 * Find the END char index of the next chunk: the LARGEST `end` (a CHAR boundary, never inside a
 * surrogate pair) such that the UTF-8 byte length of `text.slice(start, end)` is ≤
 * `EXPORT_CHUNK_BYTES`. Walks code points from `start`, summing per-code-point byte lengths, and
 * stops BEFORE the code point that would overflow the cap. Guarantees PROGRESS: a single code
 * point can be up to 4 bytes ≪ `EXPORT_CHUNK_BYTES`, so at least one code point always fits → the
 * slice advances (no infinite loop). Returns the UTF-16 char index `end` (for `text.slice`); the
 * caller encodes `text.slice(start, end)` ONCE to get the chunk's exact decoded byte length.
 */
function nextChunkBoundary(text: string, start: number): number {
  let end = start;
  let byteLength = 0;
  const total = text.length;
  while (end < total) {
    const codePoint = text.codePointAt(end);
    if (codePoint === undefined) break; // unreachable inside the bounds, defensive.
    const cpBytes = utf8ByteLengthOfCodePoint(codePoint);
    if (byteLength + cpBytes > EXPORT_CHUNK_BYTES) {
      break; // adding this code point overflows the per-chunk cap → cut before it.
    }
    byteLength += cpBytes;
    // Advance by the code point's UTF-16 unit count (2 for an astral char's surrogate pair, 1
    // otherwise) so the END index is always a valid char boundary (never mid-surrogate).
    end += codePoint > 0xffff ? 2 : 1;
  }
  return end;
}

/**
 * The DI surface for the export.dxf producer. EVERY coupling is here — the module imports
 * no editorStore, no App. S7 supplies these from the live App/store; tests inject mocks.
 */
export interface ExportDxfHandlerDeps {
  /**
   * Capture the FULL exported DXF string ONCE, synchronously ([H2]). S7 supplies
   * `() => exportedDxfTextRef.current()` (`App.tsx`:1432). Called EXACTLY ONCE per export,
   * up front — the producer slices every chunk from the returned IMMUTABLE string and NEVER
   * calls this again mid-stream (re-deriving would break committed-snapshot isolation).
   *
   * [@codex C] Returns `undefined` when the export seam is NOT wired (the `App.tsx`
   * `__editDwgExportedDxfText` window seam is absent — S7c wires it; until then it is
   * unwired). The handler surfaces an EXPLICIT not-wired reject (`browser-dispatch-error` →
   * `export-error` on the export wire) rather than streaming a silent `ok:true` EMPTY DXF
   * that the agent would mistake for a real (empty) export. An EMPTY STRING (`""`) is a
   * DISTINCT, valid result — a genuinely empty document — and DOES stream (one empty chunk
   * surfacing the snapshotRevision); only `undefined` (absent seam) is the not-wired reject.
   */
  getExportedDxfText: () => string | undefined;
  /**
   * Read the coarse `snapshotRevision` to pin on the stream ([H2]). Read ONCE at capture
   * (alongside the DXF string) so the agent's round-trip anchor matches the captured bytes.
   * `null` for a document with no revision counter. S7 supplies the ABB-S1 live counter.
   */
  getSnapshotRevision: () => number | null;
  /**
   * SEND an `export-chunk` frame on the wire (the client's public `sendExportChunk`, gated
   * on an open welcomed socket — S7 wires it). The producer builds the frame with the export
   * `correlationId` + its ORIGIN epoch (NOT the current welcome epoch) so a soft-rekey does
   * not epoch-drop the chunk at the companion (`exportBroker.ts`:242).
   */
  sendChunk: (frame: OutboundExportChunkFrame) => void;
  /**
   * Whether THIS export is still valid to produce: the connection is alive (welcomed, open
   * socket) AND no epoch bump superseded the ORIGIN epoch (the EPOCH-HALT gate). Re-checked
   * BEFORE every chunk. A `false` → the producer STOPS (resolves `{ok:false}` abort) and
   * emits nothing further. S7 supplies `() => client.getCurrentEpoch() === originEpoch &&
   * client.getStatus() startsWith 'connected'` (the liveness ∧ same-origin-epoch predicate).
   */
  isExportStillValid: (originEpoch: number) => boolean;
  /** A cooperative event-loop yield between chunks (DI for deterministic tests; default setTimeout 0). */
  yieldToEventLoop?: () => Promise<void>;
}

/**
 * The export producer handle the client routes control frames into. ONE per export; the
 * factory holds at most one ACTIVE handle (single-producer). `onControl` is called by the
 * client's `onExportControl` → `ExportControlHandler` DI for THIS export's correlationId.
 */
interface ActiveProducer {
  readonly correlationId: string;
  readonly originEpoch: number;
  paused: boolean;
  cancelled: boolean;
  /** Resolve the await-credit promise when a pause clears (resume / cancel). */
  wake: (() => void) | null;
}

/** The factory-created export.dxf handler + its control-frame router (S7 wires both). */
export interface ExportDxfHandler {
  /** The rpc handler wired into `AgentBridgeClientDeps.handlers["export.dxf"]`. */
  readonly handler: RpcHandler;
  /**
   * The export FLOW-CONTROL router wired into `AgentBridgeClientDeps.onExportControl`. The
   * client routes a parsed `export-pause`/`-resume`/`-cancel` (already epoch-gated by the
   * client's in-flight-origin registry) here; it matches the ACTIVE producer's correlationId
   * + toggles pause / sets cancel + wakes a paused producer. A frame for an UNKNOWN
   * correlation (no active producer / a different export) is inert.
   */
  readonly onControl: (frame: InboundExportControlFrame) => void;
  /**
   * TEARDOWN surface (S7 wires this to onClose / disconnect / onAbortInProgressTurn): force
   * the ACTIVE producer to abort, EVEN IF it is parked on a paused await-credit promise. A
   * socket DROP / epoch bump that cannot deliver an `export-cancel` frame (the agent's HTTP
   * reader disconnected → the companion dropped the WS) never reaches `onControl`, so a PAUSED
   * producer's wake would never fire and the single-producer latch would WEDGE permanently
   * (every later export.dxf returns `export-in-flight` for the page lifetime). `abort()` breaks
   * that wedge: it marks the producer cancelled, clears the pause, and WAKES it — the post-wake
   * `continue` then hits `abortBeforeChunk` → the cancel abort → the `finally` frees `active`.
   * Mirrors the `export-cancel` branch (cancel wins). Inert when there is no active producer.
   * INERT until S7 wires it — this PR adds + tests the method, it is NOT auto-wired.
   */
  readonly abort: () => void;
}

/**
 * The single ABORT-BEFORE-CHUNK guard (one truth for BOTH the main loop AND the empty-doc
 * path): re-check EPOCH-HALT / liveness + cancel BEFORE sending any chunk. Returns the abort
 * `RpcHandlerOutcome` to return, or `null` to proceed.
 *   • EPOCH-HALT / liveness (`isExportStillValid` false — an epoch bump / socket drop) →
 *     `browser-dispatch-error` (the companion already aborted the HTTP response).
 *   • cancel (`producer.cancelled`, the companion's `export-cancel`) → `request-timeout-aborted`.
 * Extracted so the two send sites can never drift on the abort contract (jscpd one-truth).
 */
function abortBeforeChunk(
  deps: ExportDxfHandlerDeps,
  producer: ActiveProducer,
  originEpoch: number,
): RpcHandlerOutcome | null {
  if (!deps.isExportStillValid(originEpoch)) {
    return {
      ok: false,
      code: "browser-dispatch-error",
      message: "The export was superseded (epoch bump / socket drop mid-export).",
    };
  }
  if (producer.cancelled) {
    return {
      ok: false,
      code: "request-timeout-aborted",
      message: "The export was cancelled by the companion (export-cancel).",
    };
  }
  return null;
}

/**
 * Encode a Uint8Array byte slice to STANDARD base64 (`+`/`/`, NOT base64url). Uses the
 * binary-string → `btoa` path (the browser-native encoder produces canonical standard
 * base64); a 3-byte-aligned slice has no interior padding (only the final slice may pad).
 */
function bytesToStandardBase64(bytes: Uint8Array): string {
  let binary = "";
  // Chunk the String.fromCharCode apply to avoid a call-stack overflow on a large slice.
  const STEP = 8192;
  for (let i = 0; i < bytes.length; i += STEP) {
    const sub = bytes.subarray(i, Math.min(i + STEP, bytes.length));
    binary += String.fromCharCode(...sub);
  }
  // `btoa` emits CANONICAL STANDARD base64 (+/), the dialect the companion validates.
  return btoa(binary);
}

/**
 * Create the export.dxf streaming producer (ABB-S6.3). Returns the rpc handler + the
 * control-frame router. SINGLE-PRODUCER: a 2nd export while one is active is refused
 * `{ok:false, code:'export-in-flight'}`. INERT until invoked (a factory).
 */
export function createExportDxfHandler(deps: ExportDxfHandlerDeps): ExportDxfHandler {
  const yieldFn = deps.yieldToEventLoop ?? microYield;
  let active: ActiveProducer | null = null;

  const onControl = (frame: InboundExportControlFrame): void => {
    const producer = active;
    if (producer === null || frame.correlationId !== producer.correlationId) {
      return; // no active producer / a different export → inert.
    }
    if (frame.type === "export-pause") {
      producer.paused = true;
    } else if (frame.type === "export-resume") {
      producer.paused = false;
      producer.wake?.(); // unblock a producer awaiting credit.
      producer.wake = null;
    } else if (frame.type === "export-cancel") {
      producer.cancelled = true;
      producer.paused = false;
      producer.wake?.(); // unblock a paused producer so it observes the cancel + aborts.
      producer.wake = null;
    }
  };

  // TEARDOWN: force the ACTIVE producer to abort even from a PAUSED await-credit park, when no
  // export-cancel frame can be delivered (socket drop / epoch bump → the agent's HTTP reader
  // disconnected → the companion dropped the WS, so onControl never fires). Mirrors the
  // export-cancel branch (cancel wins): the wake → the loop's post-pause `continue` → the
  // abortBeforeChunk cancel abort → the `finally` frees the single-producer latch (no wedge).
  const abort = (): void => {
    const producer = active;
    if (producer === null) {
      return; // no active producer → inert (a late teardown after the export settled).
    }
    producer.cancelled = true;
    producer.paused = false;
    producer.wake?.(); // break a PAUSED await so the producer observes the cancel + aborts.
    producer.wake = null;
  };

  const handler: RpcHandler = async (
    ctx: RpcHandlerContext,
  ): Promise<RpcHandlerOutcome> => {
    // SINGLE-PRODUCER: refuse a 2nd concurrent export (at most one resident string).
    if (active !== null) {
      return {
        ok: false,
        code: "export-in-flight",
        message: "Another DXF export is already in flight (single-producer per tab).",
      };
    }
    const originEpoch = ctx.sessionEpoch;
    const producer: ActiveProducer = {
      correlationId: ctx.correlationId,
      originEpoch,
      paused: false,
      cancelled: false,
      wake: null,
    };
    active = producer;
    try {
      // [H2] CAPTURE-ONCE: read the FULL DXF STRING + its snapshotRevision synchronously up
      // front. The captured STRING is IMMUTABLE; every chunk is sliced from it; it is NEVER
      // re-derived mid-stream (a non-bridge UI edit committing later does not change these bytes).
      const dxfText = deps.getExportedDxfText();
      // [@codex C] NOT-WIRED reject: `undefined` means the `App.tsx` export seam is absent (S7c
      // wires it). Surface an EXPLICIT not-wired failure on the export wire (`export-error` via
      // `serializeFailureTerminal`) rather than a silent `ok:true` EMPTY export the agent would
      // mistake for a real document. An EMPTY STRING is a DISTINCT valid result (a genuinely
      // empty document) and continues below; only `undefined` is the not-wired reject.
      if (dxfText === undefined) {
        return {
          ok: false,
          code: "browser-dispatch-error",
          message: "The DXF export seam is not wired in this build (App.tsx __editDwgExportedDxfText absent).",
        };
      }
      const snapshotRevision = deps.getSnapshotRevision();

      // INCREMENTAL ENCODE (#377 P2): we DO NOT materialize the whole UTF-8 byte array up front
      // (`new TextEncoder().encode(dxfText)` would hold a SECOND full-size copy of a hundreds-of-
      // MiB export + block the event loop before a pause/cancel could be observed). Instead we
      // slice the immutable STRING by CHAR per chunk and encode ONLY that chunk's substring to
      // bytes ON DEMAND (≤ EXPORT_CHUNK_BYTES + one boundary char resident at a time). `totalBytes`
      // is ACCUMULATED from the per-chunk byte lengths (the SUM of the DECODED chunk bytes — the
      // exact `byteLength` terminal the companion verifies). A char boundary is always a valid
      // UTF-8 boundary (`nextChunkBoundary` never splits a surrogate pair), so each chunk encodes
      // wholly + decodes back losslessly, and per-chunk standard base64 concatenates correctly
      // (the companion decodes each chunk independently then appends the DECODED bytes,
      // `exportBroker.ts`:327-333 — interior padding is fine across chunk boundaries).
      const totalChars = dxfText.length;
      let charOffset = 0;
      let totalBytes = 0;
      let firstChunk = true;
      while (charOffset < totalChars) {
        // EPOCH-HALT / liveness + cancel: re-check BEFORE every chunk (the one-truth guard).
        const abortOutcome = abortBeforeChunk(deps, producer, originEpoch);
        if (abortOutcome !== null) {
          return abortOutcome;
        }
        // FLOW CONTROL: if paused (over the 16 MiB companion credit cap), await a resume /
        // cancel before sending the next chunk. The wake fires on export-resume/cancel.
        if (producer.paused) {
          await new Promise<void>((resolve) => {
            producer.wake = resolve;
          });
          // Re-loop to re-check validity / cancel after the wake (do not send a stale chunk).
          continue;
        }
        // Find the CHAR boundary at/under the per-chunk byte cap (never mid-surrogate) + encode
        // ONLY this chunk's substring to bytes ON DEMAND — no full byte array is ever held.
        const end = nextChunkBoundary(dxfText, charOffset);
        const slice = new TextEncoder().encode(dxfText.slice(charOffset, end));
        const chunkB64 = bytesToStandardBase64(slice);
        totalBytes += slice.length; // accumulate the DECODED byteLength (the SUM the companion verifies).
        deps.sendChunk({
          type: "export-chunk",
          correlationId: producer.correlationId,
          sessionEpoch: originEpoch,
          chunk: chunkB64,
          // Pin the snapshotRevision on the FIRST chunk so the pump surfaces it via onStart
          // before the first byte ([H2]); subsequent chunks omit it (the pump reads it once).
          ...(firstChunk ? { snapshotRevision } : {}),
        });
        firstChunk = false;
        charOffset = end;
        // Cooperative yield so a large export does not monopolize the event loop (lets a
        // pause/cancel control frame be processed between chunks).
        await yieldFn();
      }

      // An EMPTY document (zero bytes) still surfaces the snapshotRevision: send one empty
      // chunk so the pump's onStart fires before complete (mirrors the companion's
      // zero-chunk-then-complete path, `exportBroker.ts`:355-360). Guard liveness first.
      if (totalBytes === 0) {
        // EPOCH-HALT / liveness + cancel before the lone empty chunk (the one-truth guard).
        const abortOutcome = abortBeforeChunk(deps, producer, originEpoch);
        if (abortOutcome !== null) {
          return abortOutcome;
        }
        deps.sendChunk({
          type: "export-chunk",
          correlationId: producer.correlationId,
          sessionEpoch: originEpoch,
          chunk: "",
          snapshotRevision,
        });
      }

      // RE-CHECK liveness / cancel IMMEDIATELY before the success terminal (#377 P2). An
      // `export-cancel` or an epoch bump / socket drop processed DURING the final `yieldFn()`
      // after the last non-empty chunk exits the loop WITHOUT re-running `abortBeforeChunk` (the
      // loop's pre-chunk guard ran before that chunk, not after the trailing yield) — so without
      // this gate the producer would send `export-complete` even though the companion already
      // aborted the HTTP export / the session re-keyed. Re-run the one-truth guard here: if it
      // now reports abort/invalid, return the abort/epoch-halt terminal instead of export-complete.
      const finalAbort = abortBeforeChunk(deps, producer, originEpoch);
      if (finalAbort !== null) {
        return finalAbort;
      }

      // Terminal: byteLength = the SUM of DECODED bytes (the UTF-8 length of the captured
      // string), NOT string.length / base64 length. onRpc → serializeSuccessTerminal →
      // export-complete; the companion verifies it equals the streamed total.
      return {
        ok: true,
        result: { byteLength: totalBytes, snapshotRevision },
      };
    } finally {
      // Release the single-producer latch on EVERY exit (success / abort / throw) so the
      // next export can run. A late control frame after this is inert (active === null).
      active = null;
    }
  };

  return { handler, onControl, abort };
}
